import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, Users, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { toast } from "sonner";
import {
  getAllMembersDetail,
  adminResetUserPassword,
  removeMember,
  deleteUserAsAdmin,
  verifyIsAdmin,
} from "@/lib/admin.functions";
import { MemberActivityRow } from "@/components/MemberActivityRow";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";
import { CreateMemberDialog } from "@/components/CreateMemberDialog";

const searchSchema = z.object({
  leagueId: fallback(z.string().uuid().optional(), undefined),
});

export const Route = createFileRoute("/_app/admin/members")({
  validateSearch: zodValidator(searchSchema),
  component: AllMembersPage,
  head: () => ({ meta: [{ title: "Members — Admin" }] }),
});

type Detail = Awaited<ReturnType<typeof getAllMembersDetail>>;

function AllMembersPage() {
  const { leagueId } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/members" });
  const { session, loading } = useAuth();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);
  const [search, setSearch] = useState("");
  const [authFilter, setAuthFilter] = useState<
    "all" | "accepted" | "pending_invite" | "never_signed_in"
  >("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "pending" | "denied">(
    "all",
  );

  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);
  const detailFn = useAuthedServerFn(getAllMembersDetail);
  const resetPwFn = useAuthedServerFn(adminResetUserPassword);
  const removeMemberFn = useAuthedServerFn(removeMember);
  const deleteUserFn = useAuthedServerFn(deleteUserAsAdmin);

  // Delete-user dialog state
  type DeleteTarget = { userId: string; email: string; name: string };
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState("");
  const [deleteReasonType, setDeleteReasonType] = useState<"user_request" | "moderation">(
    "user_request",
  );
  const [deleteReasonText, setDeleteReasonText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Password-reset confirmation — null = closed.
  const [resetTarget, setResetTarget] = useState<{
    userId: string;
    email: string;
    name: string;
  } | null>(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await detailFn({ data: { leagueId: leagueId ?? null } });
      setDetail(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load members");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (cancelled) return;
        setServerIsAdmin(res.isAdmin);
        if (res.isAdmin) await load();
      } catch {
        if (!cancelled) setServerIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, leagueId]);

  if (loading || (session && serverIsAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }
  if (!serverIsAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">Admins only</h1>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }
  if (!detail) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12 text-center text-muted-foreground">
        Loading members…
      </main>
    );
  }

  // Open the confirm dialog. Actual send happens in performReset.
  const handleReset = (userId: string, email: string, name: string) => {
    setResetTarget({ userId, email, name });
  };

  const performReset = async () => {
    if (!resetTarget) return;
    try {
      await resetPwFn({ data: { userId: resetTarget.userId } });
      toast.success("Password reset email sent");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send reset");
      throw e;
    }
  };

  const handleRemove = async (membershipId: string, leagueName: string) => {
    if (!confirm(`Remove this member from ${leagueName}?`)) return;
    try {
      await removeMemberFn({ data: { membershipId } });
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove member");
    }
  };

  const openDeleteDialog = (userId: string, email: string, name: string) => {
    setDeleteTarget({ userId, email, name });
    setDeleteEmailConfirm("");
    setDeleteReasonType("user_request");
    setDeleteReasonText("");
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteEmailConfirm("");
    setDeleteReasonText("");
  };

  const emailMatches =
    deleteTarget !== null &&
    deleteEmailConfirm.trim().toLowerCase() === deleteTarget.email.trim().toLowerCase();

  const canSubmitDelete =
    emailMatches &&
    !deleting &&
    (deleteReasonType === "user_request" || deleteReasonText.trim().length > 0);

  const handleDeleteUser = async () => {
    if (!deleteTarget || !canSubmitDelete) return;
    setDeleting(true);
    try {
      await deleteUserFn({
        data: {
          userId: deleteTarget.userId,
          emailConfirmation: deleteEmailConfirm,
          reasonType: deleteReasonType,
          reasonText:
            deleteReasonType === "moderation" ? deleteReasonText.trim() : undefined,
        },
      });
      toast.success(
        deleteReasonType === "user_request"
          ? "Account erased per user request."
          : "Account deleted.",
      );
      setDeleteTarget(null);
      setDeleteEmailConfirm("");
      setDeleteReasonText("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete user.");
    } finally {
      setDeleting(false);
    }
  };

  const setLeagueFilter = (value: string) => {
    navigate({
      search: () => (value === "__all__" ? {} : { leagueId: value }),
    });
  };

  const isFiltered = Boolean(detail.filteredLeague);
  const term = search.trim().toLowerCase();
  const filtered = detail.members.filter((m) => {
    // Status filter only applies when a league is selected
    if (isFiltered && statusFilter !== "all") {
      const inLeague = m.leagues.find((l) => l.leagueId === detail.filteredLeague!.id);
      if (!inLeague || inLeague.status !== statusFilter) return false;
    }
    // Auth lifecycle filter
    if (authFilter === "accepted" && !m.inviteAcceptedAt) return false;
    if (authFilter === "pending_invite" && m.inviteAcceptedAt) return false;
    if (authFilter === "never_signed_in" && m.lastSignInAt) return false;
    if (!term) return true;
    return (
      m.email.toLowerCase().includes(term) ||
      m.firstName.toLowerCase().includes(term) ||
      m.lastName.toLowerCase().includes(term) ||
      m.displayName.toLowerCase().includes(term) ||
      m.leagues.some((l) => l.leagueName.toLowerCase().includes(term))
    );
  });

  const acceptedCount = detail.members.filter((m) => m.inviteAcceptedAt).length;
  const neverSignedInCount = detail.members.filter((m) => !m.lastSignInAt).length;
  const now = Date.now();
  const activeNowCount = detail.members.filter(
    (m) => m.activeSessionCount > 0 && m.lastActiveAt && now - new Date(m.lastActiveAt).getTime() < 24 * 60 * 60 * 1000,
  ).length;
  const activeWeekCount = detail.members.filter(
    (m) => m.lastActiveAt && now - new Date(m.lastActiveAt).getTime() < 7 * 24 * 60 * 60 * 1000,
  ).length;

  const headerLine = isFiltered
    ? `${detail.members.length} ${detail.members.length === 1 ? "member" : "members"} in ${detail.filteredLeague!.name} · ${activeNowCount} active in last 24h · ${activeWeekCount} in last 7d`
    : `${detail.members.length} total · ${activeNowCount} active in last 24h · ${activeWeekCount} in last 7d · ${acceptedCount} accepted invite · ${neverSignedInCount} never signed in`;

  return (
    <main className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-6">
        <Link
          to={isFiltered ? "/admin/leagues/$leagueId" : "/admin"}
          params={isFiltered ? { leagueId: detail.filteredLeague!.id } : undefined}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" />{" "}
          {isFiltered ? "Back to league" : "Back to admin"}
        </Link>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              {isFiltered ? `${detail.filteredLeague!.name} — Members` : "All members"}
            </h1>
            <div className="text-xs text-muted-foreground mt-1">{headerLine}</div>
          </div>
          <div className="flex items-center gap-2">
            <CreateMemberDialog onCreated={() => load()} />
            <Button variant="ghost" size="sm" onClick={load} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input
          placeholder="Search by name, email, or league…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-sm"
        />
        <div className="flex gap-2 items-center">
          <Select value={leagueId ?? "__all__"} onValueChange={setLeagueFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filter by league" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All leagues</SelectItem>
              {detail.leagues.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLeagueFilter("__all__")}
              title="Clear league filter"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        <span className="text-xs text-muted-foreground self-center mr-1">Auth:</span>
        {(["all", "accepted", "pending_invite", "never_signed_in"] as const).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={authFilter === f ? "default" : "outline"}
            onClick={() => setAuthFilter(f)}
            className="capitalize"
          >
            {f === "pending_invite"
              ? "Pending invite"
              : f === "never_signed_in"
                ? "Never signed in"
                : f}
          </Button>
        ))}
      </div>

      {isFiltered && (
        <div className="flex flex-wrap gap-1 mb-4">
          <span className="text-xs text-muted-foreground self-center mr-1">Membership:</span>
          {(["all", "approved", "pending", "denied"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={statusFilter === f ? "default" : "outline"}
              onClick={() => setStatusFilter(f)}
              className="capitalize"
            >
              {f}
            </Button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No members match.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <MemberActivityRow
              key={m.userId}
              member={m}
              showLeagues={true}
              onResetPassword={handleReset}
              onRemove={isFiltered ? handleRemove : undefined}
              onDeleteUser={openDeleteDialog}
              onNameUpdated={load}
            />
          ))}
        </div>
      )}

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this user's account?</DialogTitle>
            <DialogDescription>
              This permanently removes their profile, league memberships, brackets,
              and bonus picks. They won't be able to sign in again. This can't be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteTarget && (
            <div className="space-y-4">
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  User
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {deleteTarget.name}
                </div>
                <div className="text-xs font-mono text-muted-foreground">
                  {deleteTarget.email}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reason for deletion</Label>
                <RadioGroup
                  value={deleteReasonType}
                  onValueChange={(v) =>
                    setDeleteReasonType(v as "user_request" | "moderation")
                  }
                  disabled={deleting}
                >
                  <div className="flex items-start gap-2 rounded-md border border-border p-3">
                    <RadioGroupItem
                      value="user_request"
                      id="reason-user-request"
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="reason-user-request"
                      className="font-normal cursor-pointer flex-1"
                    >
                      <span className="font-semibold text-foreground">
                        User requested deletion
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        GDPR/CCPA erasure. No identifying info kept in the audit log.
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start gap-2 rounded-md border border-border p-3">
                    <RadioGroupItem
                      value="moderation"
                      id="reason-moderation"
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="reason-moderation"
                      className="font-normal cursor-pointer flex-1"
                    >
                      <span className="font-semibold text-foreground">
                        Moderation / other
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Spam, abuse, etc. Logged with email + your reason for the record.
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {deleteReasonType === "moderation" && (
                <div className="space-y-1.5">
                  <Label htmlFor="reason-text">Reason (required)</Label>
                  <Textarea
                    id="reason-text"
                    value={deleteReasonText}
                    onChange={(e) => setDeleteReasonText(e.target.value)}
                    placeholder="e.g. Repeated spam in league chat after warning"
                    maxLength={500}
                    disabled={deleting}
                    rows={3}
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="confirm-email">
                  Type <span className="font-mono">{deleteTarget.email}</span> to confirm:
                </Label>
                <Input
                  id="confirm-email"
                  autoComplete="off"
                  value={deleteEmailConfirm}
                  onChange={(e) => setDeleteEmailConfirm(e.target.value)}
                  placeholder={deleteTarget.email}
                  disabled={deleting}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={!canSubmitDelete}
            >
              {deleting ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailConfirmDialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        title="Send password reset email?"
        description="The recipient will get an email with a link to set a new password."
        recipients={
          resetTarget ? [`${resetTarget.name} — ${resetTarget.email}`] : undefined
        }
        confirmLabel="Send reset email"
        onConfirm={performReset}
      />
    </main>
  );
}
